import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminTableShimmer } from '../../common/Shimmer.jsx';
import { useDestructiveAction } from '../../../contexts/DestructiveActionContext.jsx';
import { useAdminDialog } from '../../../contexts/AdminDialogContext.jsx';
import {
  hpApi, SLUG_RE, suggestSlug, toLocalInput, fromLocalInput, formatDateTime, postStatus,
} from './hpApi.js';
import {
  HpIcon, HpSelect, HpSwitch, HpField, HpCounter, HpImageField, HpVideoField, HpBadge, useHpToast, useUnsavedGuard, hpImageSrc,
} from './HpUi.jsx';

const CATEGORY_OPTIONS = [
  { value: 'press', label: 'プレスリリース' },
  { value: 'news', label: 'お知らせ' },
  { value: 'corporate', label: '企業情報' },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map((o) => [o.value, o.label]));

const STATUS_FILTERS = [
  { value: 'all', label: 'すべての状態' },
  { value: 'live', label: '公開中' },
  { value: 'scheduled', label: '予約公開' },
  { value: 'draft', label: '下書き' },
];

const BLOCK_TYPES = [
  { value: 'p', label: '段落' },
  { value: 'h', label: '見出し' },
  { value: 'ul', label: '箇条書き' },
  { value: 'note', label: '注記' },
];

const STATUS_TONE = { live: 'success', scheduled: 'info', draft: 'muted' };

const PR_TIMES = 'PR TIMES';
const SOURCE_PRESETS = [PR_TIMES, '@Press', '共同通信PRワイヤー', 'ValuePress!', 'ドリームニュース'];
const SOURCE_CUSTOM = '__custom';
const SOURCE_OPTIONS = [
  { value: '', label: '指定しない' },
  ...SOURCE_PRESETS.map((v) => ({ value: v, label: v })),
  { value: SOURCE_CUSTOM, label: 'その他（自由入力）' },
];

function isPrTimesUrl(url) {
  try {
    const host = new URL(url).hostname;
    return host === 'prtimes.jp' || host.endsWith('.prtimes.jp');
  } catch {
    return false;
  }
}

/** HP（src/lib/news.ts の newsSourceLabel）と同じ規則の、媒体名から決まるリンク文言 */
function defaultSourceLinkLabel(post) {
  const name = post.sourceName?.trim() || (isPrTimesUrl(post.sourceUrl) ? PR_TIMES : '');
  if (name === PR_TIMES) return `プレスリリース全文を見る（${PR_TIMES}）`;
  return name ? `記事全文を見る（${name}）` : '記事全文を見る';
}

/** 個別に入力した文言があればそれを優先する（HP と同じ） */
function sourceLinkLabel(post) {
  return post.sourceLinkText?.trim() || defaultSourceLinkLabel(post);
}

function SourceNameField({ value, onChange, disabled }) {
  const [custom, setCustom] = useState(() => Boolean(value) && !SOURCE_PRESETS.includes(value));
  const choose = (v) => {
    if (v === SOURCE_CUSTOM) {
      setCustom(true);
      if (SOURCE_PRESETS.includes(value)) onChange('');
    } else {
      setCustom(false);
      onChange(v);
    }
  };
  return (
    <>
      <HpSelect value={custom ? SOURCE_CUSTOM : value} options={SOURCE_OPTIONS} onChange={choose} ariaLabel="出典の媒体" disabled={disabled} />
      {custom && (
        <input className="hp-input" value={value} maxLength={60} disabled={disabled} onChange={(e) => onChange(e.target.value)} placeholder="媒体名を入力（例：日本経済新聞）" aria-label="出典の媒体名" />
      )}
    </>
  );
}

function emptyPost() {
  return {
    id: null,
    title: '',
    slug: '',
    subtitle: '',
    lead: '',
    category: 'news',
    body: [{ type: 'p', text: '' }],
    content: '',
    sourceUrl: '',
    sourceName: '',
    sourceLinkText: '',
    imageUrl: '',
    videoUrl: '',
    videoPosterUrl: '',
    published: false,
    publishedAt: new Date().toISOString(),
    scheduledPublishAt: null,
  };
}

function blockToEditable(b) {
  return b.type === 'ul' ? { type: 'ul', text: (b.items || []).join('\n') } : { type: b.type, text: b.text || '' };
}

function editableToBlock(b) {
  if (b.type === 'ul') {
    return { type: 'ul', items: b.text.split('\n').map((s) => s.trim()).filter(Boolean) };
  }
  return { type: b.type, text: b.text.trim() };
}

function isBlockEmpty(b) {
  return b.type === 'ul' ? b.items.length === 0 : !b.text;
}

// ---------- プレビュー ----------

function PostPreview({ post, blocks, siteUrl }) {
  const date = post.publishedAt ? new Date(post.publishedAt) : null;
  return (
    <article className="hp-preview">
      <div className="hp-preview__meta">
        {date && !Number.isNaN(date.getTime()) && (
          <time>{`${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`}</time>
        )}
        <span className="hp-preview__cat">{CATEGORY_LABEL[post.category]}</span>
      </div>
      <h1 className="hp-preview__title">{post.title || 'タイトル未入力'}</h1>
      {post.subtitle && <p className="hp-preview__subtitle">{post.subtitle}</p>}
      {post.lead && <p className="hp-preview__lead">{post.lead}</p>}
      {post.videoUrl && (
        <video className="hp-preview__video" src={hpImageSrc(post.videoUrl, siteUrl)} poster={hpImageSrc(post.videoPosterUrl, siteUrl) || undefined} controls playsInline preload="metadata" />
      )}
      <div className="hp-preview__body">
        {blocks.map((b, i) => {
          if (b.type === 'h') return <h2 key={i}>{b.text}</h2>;
          if (b.type === 'ul') return <ul key={i}>{b.items.map((t, j) => <li key={j}>{t}</li>)}</ul>;
          if (b.type === 'note') return <p key={i} className="hp-preview__note">{b.text}</p>;
          return <p key={i}>{b.text}</p>;
        })}
        {blocks.length === 0 && post.content && post.content.split(/\n{2,}/).map((t, i) => <p key={i}>{t}</p>)}
      </div>
      {post.sourceUrl && <p className="hp-preview__source">{sourceLinkLabel(post)}（{post.sourceUrl}）</p>}
    </article>
  );
}

// ---------- PR TIMES から取り込み ----------

const PRTIMES_URL_EXAMPLE = 'https://prtimes.jp/main/html/rd/p/000000008.000186774.html';

/**
 * prompted: HP管理を開いたときに未反映の記事を見つけて自動で開いた場合（URL 入力は出さない）
 * releases: { pending, ignored, error } — PR TIMES の企業 RSS と HP の記事を突き合わせた結果
 */
function PrTimesImportModal({ prompted = false, releases, onClose, onImported, onIgnore }) {
  const [url, setUrl] = useState('');
  const [busyUrl, setBusyUrl] = useState('');
  const [error, setError] = useState('');
  const [duplicate, setDuplicate] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const busy = Boolean(busyUrl);
  const pending = releases?.pending || [];
  const ignored = releases?.ignored || [];

  const run = async (target, allowDuplicate = false) => {
    if (!isPrTimesUrl(target)) {
      setError('PR TIMES の記事 URL を入力してください');
      return;
    }
    setBusyUrl(target);
    setError('');
    setDuplicate(null);
    try {
      const res = await hpApi.importPrTimes(target, { allowDuplicate });
      onImported(res);
    } catch (err) {
      if (err.code === 'prtimes-duplicate') setDuplicate({ url: target, message: err.message });
      else setError(err.message || '取り込みに失敗しました');
    } finally {
      setBusyUrl('');
    }
  };

  const toggleIgnore = async (item, ignore) => {
    setBusyUrl(item.url);
    setError('');
    try {
      await onIgnore(item, ignore);
    } catch (err) {
      setError(err.message || '設定を保存できませんでした');
    } finally {
      setBusyUrl('');
    }
  };

  const close = () => { if (!busy) onClose(); };

  const releaseRow = (item, ignoredRow) => (
    <li key={item.url} className="hp-import__release">
      <div className="hp-import__release-main">
        <span className="hp-import__release-date">{item.publishedAt ? formatDateTime(item.publishedAt) : '公開日不明'}</span>
        <a className="hp-import__release-title" href={item.url} target="_blank" rel="noreferrer">{item.title || item.url}<HpIcon.External /></a>
      </div>
      <div className="hp-import__release-actions">
        {ignoredRow ? (
          <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => toggleIgnore(item, false)} disabled={busy}>取り込み対象に戻す</button>
        ) : (
          <>
            <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => toggleIgnore(item, true)} disabled={busy} title="HP に載せない記事として記録し、今後は案内しません">取り込まない</button>
            <button type="button" className="hp-btn hp-btn--primary hp-btn--sm" onClick={() => run(item.url)} disabled={busy}>{busyUrl === item.url ? '取り込み中…' : '取り込む'}</button>
          </>
        )}
      </div>
    </li>
  );

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal hp-modal hp-modal--narrow" role="dialog" aria-modal="true" aria-labelledby="hp-prtimes-title">
        <div className="modal-header">
          <h2 id="hp-prtimes-title">{prompted ? 'HP に未反映のプレスリリースがあります' : 'PR TIMES から取り込む'}</h2>
          <button type="button" className="modal-close" onClick={close} aria-label="閉じる" disabled={busy}>&times;</button>
        </div>
        <form className="modal-body hp-import" onSubmit={(e) => { e.preventDefault(); run(url.trim()); }}>
          {(prompted || pending.length > 0) && (
            <section className="hp-import__section" aria-label="未反映のプレスリリース">
              <p className="hp-import__lead">
                {pending.length > 0
                  ? `PR TIMES に公開済みで、HP の記事（下書きを含む）に登録されていないプレスリリースが ${pending.length} 件あります。`
                  : '未反映のプレスリリースはありません。'}
              </p>
              {pending.length > 0 && <ul className="hp-import__releases">{pending.map((item) => releaseRow(item, false))}</ul>}
            </section>
          )}
          {!prompted && releases?.error && (
            <p className="hp-legacy__note"><HpIcon.Alert />PR TIMES の新着を確認できませんでした（{releases.error}）。URL を指定すれば取り込めます。</p>
          )}
          {!prompted && (
            <HpField label="URL を指定して取り込む" hint={error || `例：${PRTIMES_URL_EXAMPLE}`}>
              <input
                className={`hp-input${error ? ' is-invalid' : ''}`}
                value={url}
                disabled={busy}
                onChange={(e) => { setUrl(e.target.value); setError(''); setDuplicate(null); }}
                placeholder="https://prtimes.jp/main/html/rd/p/..."
                aria-label="PR TIMES の記事 URL"
                autoFocus={pending.length === 0}
              />
            </HpField>
          )}
          {prompted && error && <p className="hp-legacy__note hp-import__error" role="alert"><HpIcon.Alert />{error}</p>}
          <ul className="hp-import__list">
            <li>タイトル・サブタイトル・リード文・本文・公開日・出典を下書きに入れ、サムネイル画像を SNS 共有用画像に登録します。</li>
            <li>関係者コメント、「〜について」、会社概要の節は省きます（必要なら編集画面で追加してください）。</li>
            <li>記事はまだ保存されません。編集画面で内容と URL（スラッグ）を確認してから保存してください。</li>
          </ul>
          {duplicate && (
            <div className="hp-import__warn">
              <p className="hp-legacy__note"><HpIcon.Alert />{duplicate.message}。別の記事としてもう一度取り込むこともできます。</p>
              <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => run(duplicate.url, true)} disabled={busy}>それでも取り込む</button>
            </div>
          )}
          {ignored.length > 0 && (
            <section className="hp-import__section">
              <button type="button" className="hp-import__toggle" aria-expanded={showIgnored} onClick={() => setShowIgnored((v) => !v)}>
                <HpIcon.Down />「取り込まない」に設定したプレスリリース（{ignored.length} 件）
              </button>
              {showIgnored && <ul className="hp-import__releases">{ignored.map((item) => releaseRow(item, true))}</ul>}
            </section>
          )}
        </form>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>{prompted ? (pending.length ? 'あとで' : '閉じる') : 'キャンセル'}</button>
          {!prompted && (
            <button type="button" className="btn btn-primary" onClick={() => run(url.trim())} disabled={busy || !url.trim()}>{busyUrl && busyUrl === url.trim() ? '取り込み中…' : '取り込む'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const PRTIMES_DISMISS_KEY = 'hp-prtimes-pending-dismissed';

/** 「あとで」を押したときの未反映一覧。新しい記事が増えるまでは同じセッションで再び案内しない */
function isPendingDismissed(pending) {
  try {
    const seen = new Set(JSON.parse(sessionStorage.getItem(PRTIMES_DISMISS_KEY) || '[]'));
    return pending.every((p) => seen.has(p.url));
  } catch {
    return false;
  }
}

function dismissPending(pending) {
  try {
    sessionStorage.setItem(PRTIMES_DISMISS_KEY, JSON.stringify(pending.map((p) => p.url)));
  } catch {
    // sessionStorage が使えない環境では次回も案内する
  }
}

function draftFromImport(imported) {
  const draft = { ...imported };
  delete draft.originalTitle;
  return {
    ...emptyPost(),
    ...draft,
    publishedAt: draft.publishedAt || new Date().toISOString(),
    body: draft.body?.length ? draft.body : [{ type: 'p', text: '' }],
    published: false,
  };
}

// ---------- 編集 ----------

function PostEditor({ initial, initialDirty = false, siteUrl, canWrite, onBack, onSaved, onDeleted }) {
  const toast = useHpToast();
  const { showConfirm, showChoice } = useAdminDialog();
  const { requestDestructiveAction } = useDestructiveAction();
  const [post, setPost] = useState(() => ({ ...initial }));
  const [blocks, setBlocks] = useState(() => (initial.body?.length ? initial.body.map(blockToEditable) : (initial.content ? [] : [{ type: 'p', text: '' }])));
  const [scheduled, setScheduled] = useState(Boolean(initial.scheduledPublishAt));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(initialDirty);
  const [fetchingImage, setFetchingImage] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  useUnsavedGuard(dirty);

  const isNew = !initial.id;
  const set = (patch) => { setPost((p) => ({ ...p, ...patch })); setDirty(true); };
  const setBlock = (i, patch) => { setBlocks((list) => list.map((b, j) => (j === i ? { ...b, ...patch } : b))); setDirty(true); };
  const moveBlock = (i, d) => {
    setBlocks((list) => {
      const j = i + d;
      if (j < 0 || j >= list.length) return list;
      const next = [...list];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };
  const removeBlock = (i) => { setBlocks((list) => list.filter((_, j) => j !== i)); setDirty(true); };
  const addBlock = (type, at = blocks.length) => {
    setBlocks((list) => [...list.slice(0, at), { type, text: '' }, ...list.slice(at)]);
    setDirty(true);
  };

  const finalBlocks = useMemo(() => blocks.map(editableToBlock).filter((b) => !isBlockEmpty(b)), [blocks]);
  const slugError = post.slug && !SLUG_RE.test(post.slug) ? '半角英小文字・数字・ハイフンのみ使えます（先頭と末尾のハイフン、連続ハイフンは不可）' : '';
  const status = postStatus({ ...post, scheduledPublishAt: scheduled ? post.scheduledPublishAt : null });
  const publicUrl = post.slug && SLUG_RE.test(post.slug) ? `${siteUrl.replace(/\/$/, '')}/news/${post.slug}/` : '';

  const convertLegacy = () => {
    const paragraphs = post.content.split(/\n{2,}/).map((t) => t.trim()).filter(Boolean);
    setBlocks(paragraphs.map((text) => ({ type: 'p', text })));
    setDirty(true);
    toast.info('旧形式の本文を段落ブロックに変換しました。保存すると反映されます');
  };

  /** leave: 保存できたら一覧へ戻る */
  const save = async ({ leave = false } = {}) => {
    if (!post.title.trim()) return toast.error('タイトルを入力してください');
    if (!SLUG_RE.test(post.slug)) return toast.error('URL（スラッグ）を正しく入力してください');
    if (post.published && finalBlocks.length === 0 && !post.content.trim()) return toast.error('公開する記事には本文が必要です');
    if (scheduled && !post.scheduledPublishAt) return toast.error('予約公開の日時を入力してください');
    if (!isNew && initial.slug !== post.slug && initial.published) {
      const ok = await showConfirm({
        title: 'URL を変更しますか',
        message: `公開済みの記事の URL が /news/${initial.slug}/ から /news/${post.slug}/ に変わります。記事を作成したときの URL からは新しい URL へ自動で転送されますが、それ以外の旧 URL は表示できなくなります。`,
        confirmLabel: '変更して保存',
        danger: true,
      });
      if (!ok) return undefined;
    }
    setSaving(true);
    try {
      const payload = {
        title: post.title,
        slug: post.slug,
        subtitle: post.subtitle,
        lead: post.lead,
        category: post.category,
        body: finalBlocks,
        content: finalBlocks.length ? '' : post.content,
        sourceUrl: post.sourceUrl,
        sourceName: post.sourceUrl.trim() ? (post.sourceName || '').trim() : '',
        sourceLinkText: post.sourceUrl.trim() ? (post.sourceLinkText || '').trim() : '',
        imageUrl: post.imageUrl,
        videoUrl: post.videoUrl || '',
        videoPosterUrl: post.videoUrl ? (post.videoPosterUrl || '') : '',
        published: post.published,
        publishedAt: post.publishedAt,
        scheduledPublishAt: scheduled ? post.scheduledPublishAt : null,
      };
      if (isNew) {
        const res = await hpApi.createPost(payload);
        toast.success(post.published ? '記事を作成し、公開しました' : '記事を下書きとして保存しました');
        setDirty(false);
        onSaved(res.id, { leave });
      } else {
        await hpApi.updatePost(initial.id, payload);
        toast.success('記事を保存しました');
        setDirty(false);
        onSaved(initial.id, { leave });
      }
    } catch (err) {
      toast.error(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const remove = async () => {
    const result = await requestDestructiveAction({
      title: '記事を削除',
      message: `「${initial.title}」を削除します。HP からも即時に表示されなくなり、元に戻せません。`,
      confirmLabel: '削除する',
    });
    if (!result) return;
    try {
      await hpApi.deletePost(initial.id, result.reason);
      toast.success('記事を削除しました');
      setDirty(false);
      onDeleted();
    } catch (err) {
      toast.error(err.message || '削除に失敗しました');
    }
  };

  const fetchPrTimesImage = async () => {
    if (post.imageUrl) {
      const ok = await showConfirm({ title: '画像を差し替えますか', message: '設定中の SNS 共有用画像を PR TIMES のサムネイル画像に差し替えます。', confirmLabel: '差し替える' });
      if (!ok) return;
    }
    setFetchingImage(true);
    try {
      const res = await hpApi.importPrTimes(post.sourceUrl.trim(), { mode: 'image' });
      set({ imageUrl: res.imageUrl });
      toast.success('PR TIMES のサムネイル画像を取得しました（保存すると反映されます）');
    } catch (err) {
      toast.error(err.message || '画像の取得に失敗しました');
    } finally {
      setFetchingImage(false);
    }
  };

  const back = async () => {
    if (saving) return undefined;
    if (dirty) {
      const choice = await showChoice({
        title: '変更を保存しますか',
        message: '保存していない変更があります。',
        choices: [
          { value: 'discard', label: '破棄して戻る', danger: true },
          ...(canWrite ? [{ value: 'save', label: '保存して戻る', primary: true }] : []),
        ],
      });
      if (choice === 'save') return save({ leave: true });
      if (choice !== 'discard') return undefined;
    }
    onBack();
    return undefined;
  };

  return (
    <div className="hp-editor">
      <div className="hp-editor__bar">
        <button type="button" className="hp-btn hp-btn--text" onClick={back}><HpIcon.Back />一覧へ戻る</button>
        <div className="hp-editor__bar-right">
          <HpBadge tone={STATUS_TONE[status.key]}>{status.label}</HpBadge>
          {dirty && <span className="hp-unsaved">未保存の変更があります</span>}
          <button type="button" className="hp-btn hp-btn--ghost" onClick={() => setShowPreview((v) => !v)}><HpIcon.Eye />{showPreview ? 'プレビューを隠す' : 'プレビュー'}</button>
          {!isNew && canWrite && <button type="button" className="hp-btn hp-btn--danger-ghost" onClick={remove}><HpIcon.Trash />削除</button>}
          {canWrite && <button type="button" className="hp-btn hp-btn--primary" onClick={() => save()} disabled={saving}>{saving ? '保存中…' : '保存'}</button>}
        </div>
      </div>

      <div className={`hp-editor__grid${showPreview ? '' : ' is-single'}`}>
        <div className="hp-editor__form">
          <section className="hp-card">
            <h3 className="hp-card__title">基本情報</h3>
            <HpField label="タイトル" required counter={<HpCounter value={post.title} max={200} />}>
              <input className="hp-input" value={post.title} disabled={!canWrite} onChange={(e) => set({ title: e.target.value })} placeholder="例：〇〇と業務提携しました" />
            </HpField>
            <HpField
              label="URL（スラッグ）"
              required
              hint={slugError || (publicUrl ? `公開URL：${publicUrl}` : '記事のURLになります。半角英小文字・数字・ハイフンで入力してください')}
            >
              <div className="hp-input-group">
                <span className="hp-input-group__prefix">/news/</span>
                <input
                  className={`hp-input${slugError ? ' is-invalid' : ''}`}
                  value={post.slug}
                  disabled={!canWrite}
                  onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  onBlur={() => post.slug && set({ slug: suggestSlug(post.slug) })}
                  placeholder="nng-partnership"
                />
                <span className="hp-input-group__suffix">/</span>
              </div>
            </HpField>
            <div className="hp-row">
              <HpField label="カテゴリ">
                <HpSelect value={post.category} options={CATEGORY_OPTIONS} onChange={(v) => set({ category: v })} ariaLabel="カテゴリ" disabled={!canWrite} />
              </HpField>
              <HpField label="公開日（記事に表示される日付）" required>
                <input type="datetime-local" className="hp-input" value={toLocalInput(post.publishedAt)} disabled={!canWrite} onChange={(e) => set({ publishedAt: fromLocalInput(e.target.value) })} />
              </HpField>
            </div>
            <HpField label="サブタイトル" counter={<HpCounter value={post.subtitle} max={400} />}>
              <textarea className="hp-textarea" rows={2} value={post.subtitle} disabled={!canWrite} onChange={(e) => set({ subtitle: e.target.value })} />
            </HpField>
            <HpField label="リード文" hint="一覧と検索結果の説明文に使われます。未入力の場合は本文の最初の段落が使われます" counter={<HpCounter value={post.lead} max={1000} />}>
              <textarea className="hp-textarea" rows={3} value={post.lead} disabled={!canWrite} onChange={(e) => set({ lead: e.target.value })} />
            </HpField>
          </section>

          <section className="hp-card">
            <div className="hp-card__head">
              <h3 className="hp-card__title">本文</h3>
              <span className="hp-card__sub">{finalBlocks.length} ブロック</span>
            </div>
            {post.content && blocks.length === 0 && (
              <div className="hp-legacy">
                <p className="hp-legacy__note"><HpIcon.Alert />この記事は旧形式（テキスト一括）の本文です。そのまま表示されます。編集する場合はブロックに変換してください。</p>
                <textarea className="hp-textarea" rows={6} value={post.content} readOnly />
                {canWrite && <button type="button" className="hp-btn hp-btn--ghost" onClick={convertLegacy}>段落ブロックに変換</button>}
              </div>
            )}
            <ol className="hp-blocks">
              {blocks.map((b, i) => (
                <li key={i} className={`hp-block hp-block--${b.type}`}>
                  <div className="hp-block__head">
                    <span className="hp-block__no">{i + 1}</span>
                    <div className="hp-block__type">
                      <HpSelect value={b.type} options={BLOCK_TYPES} onChange={(v) => setBlock(i, { type: v })} ariaLabel={`ブロック${i + 1}の種類`} disabled={!canWrite} />
                    </div>
                    {canWrite && (
                      <div className="hp-block__tools">
                        <button type="button" className="hp-icon-btn" onClick={() => moveBlock(i, -1)} disabled={i === 0} aria-label="上へ"><HpIcon.Up /></button>
                        <button type="button" className="hp-icon-btn" onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} aria-label="下へ"><HpIcon.Down /></button>
                        <button type="button" className="hp-icon-btn" onClick={() => addBlock('p', i + 1)} aria-label="下に段落を追加"><HpIcon.Plus /></button>
                        <button type="button" className="hp-icon-btn hp-icon-btn--danger" onClick={() => removeBlock(i)} aria-label="ブロックを削除"><HpIcon.Trash /></button>
                      </div>
                    )}
                  </div>
                  {b.type === 'h' ? (
                    <input className="hp-input hp-input--heading" value={b.text} disabled={!canWrite} onChange={(e) => setBlock(i, { text: e.target.value })} placeholder="見出し" />
                  ) : (
                    <textarea
                      className="hp-textarea"
                      rows={b.type === 'ul' ? 4 : 3}
                      value={b.text}
                      disabled={!canWrite}
                      onChange={(e) => setBlock(i, { text: e.target.value })}
                      placeholder={b.type === 'ul' ? '1行に1項目を入力' : b.type === 'note' ? '注記（※から始まる補足など）' : '段落'}
                    />
                  )}
                </li>
              ))}
            </ol>
            {canWrite && (
              <div className="hp-add-blocks">
                <span>ブロックを追加：</span>
                {BLOCK_TYPES.map((t) => (
                  <button key={t.value} type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => addBlock(t.value)}><HpIcon.Plus size={12} />{t.label}</button>
                ))}
              </div>
            )}
          </section>

          <section className="hp-card">
            <h3 className="hp-card__title">動画</h3>
            <HpField label="記事の動画" hint="リード文の下、本文の前に表示します">
              <HpVideoField value={post.videoUrl || ''} poster={post.videoPosterUrl || ''} onChange={(url) => set({ videoUrl: url })} siteUrl={siteUrl} disabled={!canWrite} />
            </HpField>
            {post.videoUrl && (
              <HpField label="再生前に表示する画像" hint="未設定のままだと、ブラウザによっては再生するまで黒い画面になります（推奨 16:9）">
                <HpImageField value={post.videoPosterUrl || ''} onChange={(url) => set({ videoPosterUrl: url })} folder="posts" siteUrl={siteUrl} disabled={!canWrite} aspect="16 / 9" />
              </HpField>
            )}
          </section>

          <section className="hp-card">
            <h3 className="hp-card__title">公開設定</h3>
            <HpSwitch
              checked={post.published}
              disabled={!canWrite}
              onChange={(v) => set({ published: v })}
              label="HP に公開する"
              description="オフの間は下書きとして保存され、HP には表示されません"
            />
            <HpSwitch
              checked={scheduled}
              disabled={!canWrite}
              onChange={(v) => { setScheduled(v); if (v && !post.scheduledPublishAt) set({ scheduledPublishAt: new Date(Date.now() + 3600e3).toISOString() }); else setDirty(true); }}
              label="予約公開"
              description="指定した日時になるまで HP に表示しません（公開がオンのときのみ有効）"
            />
            {scheduled && (
              <HpField label="公開する日時">
                <input type="datetime-local" className="hp-input" value={toLocalInput(post.scheduledPublishAt)} disabled={!canWrite} onChange={(e) => set({ scheduledPublishAt: fromLocalInput(e.target.value) })} />
              </HpField>
            )}
            <p className="hp-field__hint">HP への反映は保存後およそ1分以内です。</p>
          </section>

          <section className="hp-card">
            <h3 className="hp-card__title">関連情報</h3>
            <div className="hp-row">
              <HpField label="出典URL（PR TIMES など）" hint="https:// から始まる URL">
                <input
                  className="hp-input"
                  value={post.sourceUrl}
                  disabled={!canWrite}
                  onChange={(e) => set({ sourceUrl: e.target.value })}
                  onBlur={() => { if (!post.sourceName && isPrTimesUrl(post.sourceUrl)) set({ sourceName: PR_TIMES }); }}
                  placeholder="https://prtimes.jp/..."
                />
              </HpField>
              <HpField
                label="出典の媒体"
                hint={post.sourceUrl ? `HP のリンク表示：${sourceLinkLabel(post)}` : '出典URLを入れると、記事の末尾に媒体名付きのリンクを表示します'}
              >
                <SourceNameField
                  key={post.id || 'new'}
                  value={post.sourceName || ''}
                  onChange={(v) => set({ sourceName: v })}
                  disabled={!canWrite}
                />
              </HpField>
            </div>
            {post.sourceUrl.trim() && (
              <HpField
                label="リンクの表示文言（任意）"
                hint={`未入力の場合は「${defaultSourceLinkLabel(post)}」と表示します。セミナー案内など記事以外へのリンクで使います`}
                counter={<HpCounter value={post.sourceLinkText || ''} max={60} />}
              >
                <input
                  className="hp-input"
                  value={post.sourceLinkText || ''}
                  maxLength={60}
                  disabled={!canWrite}
                  onChange={(e) => set({ sourceLinkText: e.target.value })}
                  placeholder="例：セミナー詳細を見る（東京デンタルショー2026）"
                />
              </HpField>
            )}
            <HpField label="SNS共有用画像" hint="SNS でシェアされたときに表示される画像です（推奨 1200×630）">
              <HpImageField value={post.imageUrl} onChange={(url) => set({ imageUrl: url })} folder="posts" siteUrl={siteUrl} disabled={!canWrite || fetchingImage} aspect="1200 / 630" />
              {canWrite && isPrTimesUrl(post.sourceUrl.trim()) && (
                <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm hp-import__image-btn" onClick={fetchPrTimesImage} disabled={fetchingImage}>
                  <HpIcon.Image size={14} />{fetchingImage ? '取得中…' : 'PR TIMES のサムネイル画像を取得'}
                </button>
              )}
            </HpField>
          </section>
        </div>

        {showPreview && (
          <aside className="hp-editor__preview">
            <div className="hp-editor__preview-head">
              <span>プレビュー</span>
              {!isNew && initial.published && publicUrl && (
                <a href={publicUrl} target="_blank" rel="noreferrer" className="hp-link"><HpIcon.External />HPで見る</a>
              )}
            </div>
            <PostPreview post={post} blocks={finalBlocks} siteUrl={siteUrl} />
          </aside>
        )}
      </div>
    </div>
  );
}

// ---------- 一覧 ----------

export default function HpPostsPanel({ siteUrl, canWrite }) {
  const toast = useHpToast();
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [editingImported, setEditingImported] = useState(false);
  const [importMode, setImportMode] = useState(null);
  const [releases, setReleases] = useState(null);
  const editingRef = useRef(null);
  editingRef.current = editing;

  const openEditor = (post, imported = false) => {
    setEditingImported(imported);
    setEditing(post);
  };

  const checkReleases = useCallback(async ({ prompt = false } = {}) => {
    try {
      const res = await hpApi.pendingPrTimes();
      const next = { pending: res.pending || [], ignored: res.ignored || [], error: '' };
      setReleases(next);
      if (prompt && !editingRef.current && next.pending.length && !isPendingDismissed(next.pending)) setImportMode((m) => m || 'prompt');
    } catch (err) {
      // 自動確認の失敗では作業を妨げない（手動で開いたモーダルに理由を出す）
      setReleases((prev) => ({ pending: prev?.pending || [], ignored: prev?.ignored || [], error: err.message || '取得に失敗しました' }));
    }
  }, []);

  useEffect(() => {
    if (canWrite) checkReleases({ prompt: true });
  }, [canWrite, checkReleases]);

  const closeImport = () => {
    if (importMode === 'prompt' && releases?.pending.length) dismissPending(releases.pending);
    setImportMode(null);
  };

  const onIgnore = async (item, ignore) => {
    await hpApi.setPrTimesIgnored(item.url, ignore, item.title);
    setReleases((prev) => {
      const pending = (prev?.pending || []).filter((r) => r.url !== item.url);
      const ignored = (prev?.ignored || []).filter((r) => r.url !== item.url);
      const byDate = (a, b) => String(b.publishedAt || '').localeCompare(String(a.publishedAt || ''));
      return ignore
        ? { ...prev, pending, ignored: [item, ...ignored].sort(byDate) }
        : { ...prev, pending: [item, ...pending].sort(byDate), ignored };
    });
    toast.success(ignore ? '「取り込まない」に設定しました。今後は案内しません' : '取り込み対象に戻しました');
  };

  const onImported = (res) => {
    setImportMode(null);
    openEditor(draftFromImport(res.draft), true);
    toast.success('PR TIMES の記事から下書きを作成しました。内容を確認して保存してください');
    if (res.omittedSections?.length) toast.info(`省いた節：${res.omittedSections.join('／')}`);
    if (res.skippedTables) toast.info(`表 ${res.skippedTables} 件は取り込めないため省きました`);
    if (res.imageError) toast.error(`サムネイル画像は登録できませんでした：${res.imageError}`);
  };

  const load = useCallback(async () => {
    try {
      const res = await hpApi.listPosts();
      setPosts(res.posts || []);
      return res.posts || [];
    } catch (err) {
      toast.error(err.message || '記事の取得に失敗しました');
      setPosts([]);
      return [];
    }
  }, [toast]);

  useEffect(() => {
    hpApi.listPosts()
      .then((res) => setPosts(res.posts || []))
      .catch((err) => { toast.error(err.message || '記事の取得に失敗しました'); setPosts([]); });
  }, [toast]);

  const visible = useMemo(() => {
    if (!posts) return [];
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (filter !== 'all' && postStatus(p).key !== filter) return false;
      if (q && !`${p.title} ${p.slug}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [posts, filter, query]);

  if (editing) {
    return (
      <PostEditor
        key={editing.id || 'new'}
        initial={editing}
        initialDirty={editingImported}
        siteUrl={siteUrl}
        canWrite={canWrite}
        onBack={() => openEditor(null)}
        onSaved={async (id, { leave = false } = {}) => {
          const list = await load();
          const fresh = list.find((p) => p.id === id);
          openEditor(leave ? null : fresh || null);
          if (canWrite) checkReleases();
        }}
        onDeleted={async () => { openEditor(null); await load(); if (canWrite) checkReleases(); }}
      />
    );
  }

  return (
    <div className="hp-panel">
      <div className="hp-toolbar">
        <input className="hp-input hp-toolbar__search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="タイトル・URLで検索" aria-label="記事を検索" />
        <div className="hp-toolbar__filter">
          <HpSelect value={filter} options={STATUS_FILTERS} onChange={setFilter} ariaLabel="状態で絞り込み" />
        </div>
        {canWrite && (
          <button
            type="button"
            className="hp-btn hp-btn--ghost"
            onClick={() => setImportMode('manual')}
            aria-label={releases?.pending.length ? `PR TIMES から取り込む（未反映 ${releases.pending.length} 件）` : undefined}
          >
            <HpIcon.External />PR TIMES から取り込む
            {releases?.pending.length > 0 && <span className="hp-count" aria-hidden="true">{releases.pending.length}</span>}
          </button>
        )}
        {canWrite && (
          <button type="button" className="hp-btn hp-btn--primary" onClick={() => openEditor(emptyPost())}><HpIcon.Plus />新しい記事</button>
        )}
      </div>
      {importMode && (
        <PrTimesImportModal
          prompted={importMode === 'prompt'}
          releases={releases}
          onClose={closeImport}
          onImported={onImported}
          onIgnore={onIgnore}
        />
      )}

      {posts === null ? (
        <AdminTableShimmer rows={4} columns={4} />
      ) : visible.length === 0 ? (
        <div className="hp-empty">{posts.length === 0 ? 'まだ記事がありません。「新しい記事」から作成できます。' : '条件に合う記事がありません。'}</div>
      ) : (
        <ul className="hp-list">
          {visible.map((p) => {
            const st = postStatus(p);
            return (
              <li key={p.id}>
                <button type="button" className="hp-list__item" onClick={() => openEditor(p)}>
                  <div className="hp-list__main">
                    <div className="hp-list__badges">
                      <HpBadge tone={STATUS_TONE[st.key]}>{st.label}</HpBadge>
                      <HpBadge>{CATEGORY_LABEL[p.category]}</HpBadge>
                      {!p.body.length && p.content && <HpBadge tone="warning">旧形式</HpBadge>}
                    </div>
                    <span className="hp-list__title">{p.title || '（タイトルなし）'}</span>
                    <span className="hp-list__sub">/news/{p.slug}/</span>
                  </div>
                  <div className="hp-list__side">
                    <span>公開日 {formatDateTime(p.publishedAt)}</span>
                    {p.scheduledPublishAt && <span>予約 {formatDateTime(p.scheduledPublishAt)}</span>}
                    <span className="hp-list__muted">更新 {formatDateTime(p.updatedAt)}</span>
                  </div>
                  <HpIcon.Edit />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
